import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  ResponseArtifactDTO,
  ResponseArtifactCreatedEvent,
  ResponseArtifactUpdatedEvent,
  UserTodoItem,
  GetUserArtifactsResponse,
  GetUserTodosResponse,
  UpdateResponseArtifactRequest,
  UpdateResponseArtifactResponse,
  AgentEvents,
  UserTodoUpdatedEvent,
} from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import { createPortal } from 'react-dom';
import api from '../../../api';
import {
  useResponseArtifactEvents,
  useOnboardingModals,
} from '../../../providers';
import { usePusher } from '../../../contexts/PusherContext';
import { usePlanModal } from '../../../providers/PlanModalProvider';
import TasksSkeleton from './TasksSkeleton';
import ErrorState from '../../common/ErrorState';
import ResponseArtifactModal from './ResponseArtifactModal';
import { useMobileNav } from '../../../contexts/MobileNavContext';
import ResearchStillThereModal from './ResearchStillThereModal';
import { ReactComponent as ClockIcon } from '../../../icons/clock-arrow.svg';
import { ReactComponent as PlusAddIcon } from '../../../icons/plus-add.svg';
import { ReactComponent as CalendarOutlineIcon } from '../../../icons/calendar-outline.svg';
import { ReactComponent as DocumentTextIcon } from '../../../icons/document-text.svg';
import { ReactComponent as VerticalDotsIcon } from '../../../icons/vertical-dots.svg';
import './your-tasks-list.scss';

/** Get the "Open X" label for an artifact based on its type. */
const getOpenLabel = (artifact: ResponseArtifactDTO): string | null => {
  const type = artifact.artifactType as string;
  if (type === 'form_input')
    return artifact.confirmationId ? 'Open Approval' : 'Open Form';
  if (type === 'spending_approval') return 'Open Approval';
  if (type === 'multiple_choice') return 'Open Options';
  if (type === 'yes_or_no') return 'Open Question';
  if (type === 'confirmation_query') return 'Open Question';
  return null;
};

/** Check whether a due date is in the past. */
const isOverdue = (dueDate: string): boolean => new Date(dueDate) < new Date();

/** Priority colour mapping (dot fill). */
const PRIORITY_COLORS: Record<string, string> = {
  high: '#EF4444',
  medium: '#F59E0B',
  low: '#3B82F6',
};

/** Format a due date, including time when it's not midnight. */
const formatDueDate = (dueDate: string): string => {
  const d = new Date(dueDate);
  const datePart = d.toLocaleDateString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  if (d.getHours() === 0 && d.getMinutes() === 0) return datePart;
  const timePart = d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .replace(':00', '');
  return `${datePart} at ${timePart}`;
};

interface YourTasksListProps {
  onNavigateToThread?: (
    threadId: string,
    parentThreadId?: string,
    researchAppId?: string,
  ) => void;
  onAddTodo?: () => void;
  optimisticUserTodo?: UserTodoItem | null;
  confirmedTodoIds?: { optimisticId: string; realId: string } | null;
  removedUserTodoId?: string | null;
  threadId?: string | null;
  agentId?: string;
  onOverlayChange?: (isOpen: boolean) => void;
  onTodoCreated?: (todo: UserTodoItem) => void;
}

/** Derive a description tag based on the artifact/todo type. */
const getItemTypeLabel = (
  item: ResponseArtifactDTO | UserTodoItem,
  kind: 'artifact' | 'userTodo',
): string => {
  if (kind === 'userTodo') return 'Todo';
  const artifact = item as ResponseArtifactDTO;
  const type = artifact.artifactType as string;
  if (type === 'multiple_choice') return 'Make a choice';
  if (type === 'yes_or_no') return 'Answer question';
  if (type === 'form_input')
    return artifact.confirmationId ? 'Approve this' : 'Fill in form';
  if (type === 'spending_approval') return 'Approve spend';
  if (type === 'blank_todo') return 'Todo';
  if (type === 'confirmation_query') return 'Confirm';
  return 'Todo';
};

/** Get the project name to display (parent thread), excluding inner monologue and inbound email. */
const getProjectName = (
  item: ResponseArtifactDTO | UserTodoItem,
  kind: 'artifact' | 'userTodo',
): string | null => {
  if (kind === 'userTodo') {
    const todo = item as UserTodoItem;
    if (!todo.threadDescription) return null;
    return todo.threadDescription;
  }
  const artifact = item as ResponseArtifactDTO;
  if (artifact.parentThreadIsInternalMonologue) return null;
  if ((artifact as any).parentThreadIsRehomedTask) return null;
  if (artifact.parentThreadIsMainThread) return null;
  // Exclude inbound email threads (response artifact threads)
  if (artifact.parentThreadIsResponseArtifactThread) return null;
  return artifact.parentThreadDescription || null;
};

type UnifiedItem =
  | { kind: 'userTodo'; data: UserTodoItem }
  | { kind: 'artifact'; data: ResponseArtifactDTO };

export interface YourTasksListHandle {
  refresh: () => Promise<void>;
}

export const YourTasksList = forwardRef<
  YourTasksListHandle,
  YourTasksListProps
>(function YourTasksList(
  {
    onNavigateToThread,
    onAddTodo,
    optimisticUserTodo,
    confirmedTodoIds,
    removedUserTodoId,
    threadId,
    agentId,
    onOverlayChange,
    onTodoCreated,
  },
  ref,
) {
  const { getAccessTokenSilently } = useAuth0();
  const { hasSeenModal } = useOnboardingModals();
  const { openPlanModal } = usePlanModal();
  const { isMobile } = useMobileNav();
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;

  // Data state
  const [pendingArtifacts, setPendingArtifacts] = useState<
    ResponseArtifactDTO[]
  >([]);
  const [actionedArtifacts, setActionedArtifacts] = useState<
    ResponseArtifactDTO[]
  >([]);
  const [userTodos, setUserTodos] = useState<UserTodoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Animation state
  const [completingItems, setCompletingItems] = useState<Set<string>>(
    new Set(),
  );
  const completingItemsRef = useRef(completingItems);
  completingItemsRef.current = completingItems;
  const [submittedItems, setSubmittedItems] = useState<Set<string>>(new Set());
  const submittedItemsRef = useRef(submittedItems);
  submittedItemsRef.current = submittedItems;

  const todoSound = useMemo(() => new Audio('/todo-sound.mp3'), []);
  const playTodoSound = useCallback(() => {
    todoSound.currentTime = 0;
    todoSound.play().catch(() => {});
  }, [todoSound]);

  const startCompletingAnimation = useCallback(
    (todoId: string, element: HTMLElement | null, skipSound = false) => {
      if (!skipSound) playTodoSound();
      setCompletingItems((prev) => new Set(prev).add(todoId));

      // After the fade completes (400ms delay + 300ms fade), add collapsed class for smooth height transition
      const todoEl = element?.closest('.your-task-item') as HTMLElement | null;
      if (todoEl) {
        setTimeout(() => {
          todoEl.classList.add('collapsed');
        }, 700);
      }
    },
    [playTodoSound],
  );

  // Dropdown menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({
    top: 0,
    right: 0,
  });
  const menuRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleMenu = useCallback(
    (id: string, btnEl: HTMLElement) => {
      if (openMenuId === id) {
        setOpenMenuId(null);
        return;
      }
      const rect = btnEl.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
      setOpenMenuId(id);
    },
    [openMenuId],
  );

  // Close menu when clicking outside or scrolling
  useEffect(() => {
    if (!openMenuId) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setOpenMenuId(null);
    };
    const handleScroll = () => setOpenMenuId(null);
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [openMenuId]);

  // Artifact modal state
  const [artifactModalOpen, setArtifactModalOpen] = useState(false);
  const [fullArtifact, setFullArtifact] = useState<ResponseArtifactDTO | null>(
    null,
  );
  const [loadingArtifact, setLoadingArtifact] = useState(false);

  // Research still-there modal state (blank todos with linked research)
  const [researchModalOpen, setResearchModalOpen] = useState(false);
  const [blankTodoToComplete, setBlankTodoToComplete] =
    useState<ResponseArtifactDTO | null>(null);

  // Notify parent when a full-screen overlay is active
  useEffect(() => {
    onOverlayChange?.(artifactModalOpen);
  }, [artifactModalOpen, onOverlayChange]);

  // Memoize artifacts array for modal
  const modalArtifacts = useMemo(
    () => (fullArtifact ? [fullArtifact] : []),
    [fullArtifact],
  );

  // ---------- Data fetching ----------

  const fetchData = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const threadFilter = threadId ? `&threadId=${threadId}` : '';
        const parentThreadFilter = threadId
          ? `&parentThreadId=${threadId}`
          : '';
        const agentFilter = agentId ? `&agentId=${agentId}` : '';

        const [pendingResponse, actionedResponse, userTodosResponse] =
          await Promise.all([
            api.get<GetUserArtifactsResponse>(
              `/response-artifacts/user/artifacts?status=pending${parentThreadFilter}${agentFilter}`,
              { headers: { Authorization: `Bearer ${token}` } },
            ),
            api.get<GetUserArtifactsResponse>(
              `/response-artifacts/user/artifacts?status=actioned${parentThreadFilter}${agentFilter}`,
              { headers: { Authorization: `Bearer ${token}` } },
            ),
            api.get<GetUserTodosResponse>(
              `/user-todos?assigneeType=user${threadFilter}${agentFilter}`,
              { headers: { Authorization: `Bearer ${token}` } },
            ),
          ]);
        setPendingArtifacts(pendingResponse.data.artifacts || []);
        setActionedArtifacts(actionedResponse.data.artifacts || []);
        setUserTodos(userTodosResponse.data.todos || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [getAccessTokenSilently, threadId, agentId],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => fetchData({ silent: true }),
    }),
    [fetchData],
  );

  // ---------- Optimistic updates ----------

  useEffect(() => {
    if (!optimisticUserTodo) return;
    setUserTodos((prev) => {
      const withoutOptimistic = prev.filter(
        (t) => !t.id.startsWith('optimistic-'),
      );
      return [optimisticUserTodo, ...withoutOptimistic];
    });
  }, [optimisticUserTodo]);

  useEffect(() => {
    if (!confirmedTodoIds) return;
    const { optimisticId, realId } = confirmedTodoIds;
    setUserTodos((prev) =>
      prev.map((t) => (t.id === optimisticId ? { ...t, id: realId } : t)),
    );
  }, [confirmedTodoIds]);

  useEffect(() => {
    if (!removedUserTodoId) return;
    setUserTodos((prev) => prev.filter((t) => t.id !== removedUserTodoId));
  }, [removedUserTodoId]);

  // ---------- Real-time Pusher updates (artifacts) ----------

  const {
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  } = useResponseArtifactEvents();

  useEffect(() => {
    const callbackId = `your-tasks-list-${threadId || 'global'}`;

    registerCreatedCallback(
      callbackId,
      (data: ResponseArtifactCreatedEvent) => {
        if (!data.artifact) return;
        if (threadId && data.parentThreadId !== threadId) return;
        if (data.artifact.artifactType === 'information_dump') return;
        if ((data.artifact.artifactType as string) === 'suggestion') return;
        const dto: ResponseArtifactDTO = {
          ...data.artifact,
          createdAt: data.artifact.createdAt || new Date().toISOString(),
          updatedAt: data.artifact.updatedAt || new Date().toISOString(),
        };
        setPendingArtifacts((prev) => {
          if (prev.some((a) => a.id === dto.id)) return prev;
          return [dto, ...prev];
        });
      },
    );

    registerUpdatedCallback(
      callbackId,
      (data: ResponseArtifactUpdatedEvent) => {
        if (!data.artifactId) return;
        if (threadId && data.parentThreadId !== threadId) return;

        if (
          submittedItemsRef.current.has(data.artifactId) ||
          completingItemsRef.current.has(data.artifactId)
        ) {
          return;
        }

        // Content update with full artifact (e.g. enhance / find_more)
        if (data.artifact && data.status === 'pending') {
          setActionedArtifacts((prev) =>
            prev.filter((a) => a.id !== data.artifactId),
          );
          setPendingArtifacts((prev) => {
            const exists = prev.some((a) => a.id === data.artifactId);
            if (exists) {
              return prev.map((a) =>
                a.id === data.artifactId ? { ...a, ...data.artifact } : a,
              );
            }
            return [...prev, data.artifact as ResponseArtifactDTO];
          });
          return;
        }

        // Handle linked artifact updates
        if (data.linkedArtifacts) {
          setPendingArtifacts((prev) =>
            prev.map((a) =>
              a.id === data.artifactId
                ? {
                    ...a,
                    linkedArtifactIds: data.linkedArtifactIds,
                    linkedArtifacts: data.linkedArtifacts,
                  }
                : a,
            ),
          );
          return;
        }

        // Field-only updates (priority, dueDate)
        if (
          (data as any).priority !== undefined ||
          (data as any).dueDate !== undefined
        ) {
          setPendingArtifacts((prev) =>
            prev.map((a) => {
              if (a.id !== data.artifactId) return a;
              const updates: Partial<ResponseArtifactDTO> = {};
              if ((data as any).priority !== undefined)
                updates.priority = (data as any).priority || undefined;
              if ((data as any).dueDate !== undefined)
                updates.dueDate = (data as any).dueDate || undefined;
              return { ...a, ...updates };
            }),
          );
          return;
        }

        if (data.status === 'actioned') {
          setPendingArtifacts((prev) =>
            prev.filter((a) => a.id !== data.artifactId),
          );
          if (!data.dismissed) {
            setActionedArtifacts((prev) => {
              if (prev.some((a) => a.id === data.artifactId)) return prev;
              return [
                {
                  id: data.artifactId,
                  status: 'actioned',
                } as ResponseArtifactDTO,
                ...prev,
              ];
            });
          }
        }
      },
    );

    return () => {
      unregisterCreatedCallback(callbackId);
      unregisterUpdatedCallback(callbackId);
    };
  }, [
    threadId,
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  ]);

  // ---------- Real-time Pusher updates (user todos) ----------

  useEffect(() => {
    if (!userChannel) return;

    const handleUserTodoUpdated = (data: UserTodoUpdatedEvent) => {
      if (!data.todoId) return;
      setUserTodos((prev) =>
        prev.map((t) => {
          if (t.id !== data.todoId) return t;
          const updates: Partial<UserTodoItem> = {};
          if ((data as any).priority !== undefined)
            updates.priority = (data as any).priority;
          if ((data as any).dueDate !== undefined)
            updates.dueDate = (data as any).dueDate;
          if ((data as any).description !== undefined)
            updates.description = (data as any).description;
          if (data.status !== undefined)
            updates.status = data.status as UserTodoItem['status'];
          return { ...t, ...updates };
        }),
      );
    };

    userChannel.bind(AgentEvents.USER_TODO_UPDATED, handleUserTodoUpdated);
    return () => {
      userChannel.unbind(AgentEvents.USER_TODO_UPDATED, handleUserTodoUpdated);
    };
  }, [userChannel]);

  // ---------- Artifact modal handlers ----------

  const openArtifactForItem = useCallback(
    async (artifact: ResponseArtifactDTO) => {
      setLoadingArtifact(true);
      setArtifactModalOpen(true);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<{
          success: boolean;
          artifact: ResponseArtifactDTO;
        }>(`/response-artifacts/artifact/${artifact.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          setFullArtifact(response.data.artifact);
        }
      } catch (err) {
        console.error('Failed to fetch artifact:', err);
      } finally {
        setLoadingArtifact(false);
      }
    },
    [getAccessTokenSilently],
  );

  const closeArtifactModal = useCallback(() => {
    setArtifactModalOpen(false);
    setFullArtifact(null);
  }, []);

  const handleOptimisticAction = useCallback(
    (artifact: ResponseArtifactDTO, action: 'submit' | 'dismiss') => {
      const animId = artifact.id;
      closeArtifactModal();

      if (action === 'submit') {
        playTodoSound();
        setSubmittedItems((prev) => new Set(prev).add(animId));

        setTimeout(() => {
          setSubmittedItems((prev) => {
            const next = new Set(prev);
            next.delete(animId);
            return next;
          });
          const todoEl = document.querySelector(
            `[data-todo-id="${animId}"]`,
          ) as HTMLElement | null;
          startCompletingAnimation(animId, todoEl, true);

          setTimeout(() => {
            setCompletingItems((prev) => {
              const next = new Set(prev);
              next.delete(animId);
              return next;
            });
            setPendingArtifacts((prev) =>
              prev.filter((a) => a.id !== artifact.id),
            );
            setActionedArtifacts((prev) => [
              { ...artifact, status: 'actioned' as const },
              ...prev,
            ]);
          }, 600);
        }, 1000);
      } else {
        const todoEl = document.querySelector(
          `[data-todo-id="${animId}"]`,
        ) as HTMLElement | null;
        startCompletingAnimation(animId, todoEl);

        setTimeout(() => {
          setCompletingItems((prev) => {
            const next = new Set(prev);
            next.delete(animId);
            return next;
          });
          setPendingArtifacts((prev) =>
            prev.filter((a) => a.id !== artifact.id),
          );
          setActionedArtifacts((prev) => [
            { ...artifact, status: 'actioned' as const },
            ...prev,
          ]);
        }, 600);
      }
    },
    [closeArtifactModal, playTodoSound, startCompletingAnimation],
  );

  const handleSubmitArtifactResponse = useCallback(
    async (
      artifact: ResponseArtifactDTO,
      response: any,
      mandateOptions?: {
        createMandate: boolean;
        mandateItems: Array<{
          actionLine: string;
          descriptionLine: string;
          price?: number;
          price_currency?: string;
        }>;
      },
      cardSelection?: {
        cardType: 'agent' | 'user_card' | 'merchant_stored';
        credentialId?: string;
        cardHint?: string;
      },
    ) => {
      try {
        const token = await getAccessTokenSilently();
        const request: UpdateResponseArtifactRequest = {
          artifactId: artifact.id,
          status: 'actioned',
          userResponse: response,
          source: 'todo_list',
          ...(mandateOptions?.createMandate && {
            create_mandate: true,
            mandate_items: mandateOptions.mandateItems,
          }),
          ...(cardSelection && { card_selection: cardSelection }),
        };
        await api.patch<UpdateResponseArtifactResponse>(
          `/response-artifacts/${artifact.id}`,
          request,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('Error submitting artifact response:', err);
      }
    },
    [getAccessTokenSilently],
  );

  // ---------- User todo toggle ----------

  const handleToggleUserTodo = useCallback(
    async (todo: UserTodoItem) => {
      if (todo.isSetupPlan && todo.status !== 'complete') {
        openPlanModal();
        return;
      }

      const newStatus = todo.status === 'pending' ? 'complete' : 'pending';

      if (newStatus === 'complete') {
        const todoEl = document.querySelector(
          `[data-todo-id="${todo.id}"]`,
        ) as HTMLElement | null;
        startCompletingAnimation(todo.id, todoEl);

        setTimeout(() => {
          setCompletingItems((prev) => {
            const next = new Set(prev);
            next.delete(todo.id);
            return next;
          });
          setUserTodos((prev) =>
            prev.map((t) =>
              t.id === todo.id ? { ...t, status: newStatus } : t,
            ),
          );
        }, 600);
      } else {
        setUserTodos((prev) =>
          prev.map((t) => (t.id === todo.id ? { ...t, status: newStatus } : t)),
        );
      }

      if (todo.id.startsWith('optimistic-')) return;

      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/user-todos/${todo.id}`,
          { status: newStatus },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('Error updating user todo:', err);
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(todo.id);
          return next;
        });
        setUserTodos((prev) =>
          prev.map((t) =>
            t.id === todo.id ? { ...t, status: todo.status } : t,
          ),
        );
      }
    },
    [getAccessTokenSilently, openPlanModal, startCompletingAnimation],
  );

  // ---------- Blank todo completion ----------

  const completeBlankTodo = useCallback(
    async (artifact: ResponseArtifactDTO) => {
      const todoEl = document.querySelector(
        `[data-todo-id="${artifact.id}"]`,
      ) as HTMLElement | null;
      startCompletingAnimation(artifact.id, todoEl);

      setTimeout(() => {
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(artifact.id);
          return next;
        });
        setPendingArtifacts((prev) => prev.filter((a) => a.id !== artifact.id));
        setActionedArtifacts((prev) => [
          { ...artifact, status: 'actioned' as const },
          ...prev,
        ]);
      }, 600);

      try {
        const token = await getAccessTokenSilently();
        const request: UpdateResponseArtifactRequest = {
          artifactId: artifact.id,
          status: 'actioned',
          userResponse: { completed: true },
          source: 'todo_list',
        };
        await api.patch<UpdateResponseArtifactResponse>(
          `/response-artifacts/${artifact.id}`,
          request,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('Error completing blank todo:', err);
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(artifact.id);
          return next;
        });
        setPendingArtifacts((prev) => {
          if (prev.some((a) => a.id === artifact.id)) return prev;
          return [artifact, ...prev];
        });
        setActionedArtifacts((prev) =>
          prev.filter((a) => a.id !== artifact.id),
        );
      }
    },
    [getAccessTokenSilently, startCompletingAnimation],
  );

  const handleToggleBlankTodo = useCallback(
    (artifact: ResponseArtifactDTO) => {
      const hasLinkedResearch = (artifact.linkedArtifacts?.length ?? 0) > 0;
      if (
        hasLinkedResearch &&
        !hasSeenModal('dismiss_blank_todo_research_warning')
      ) {
        setBlankTodoToComplete(artifact);
        setResearchModalOpen(true);
        return;
      }
      completeBlankTodo(artifact);
    },
    [completeBlankTodo, hasSeenModal],
  );

  // ---------- Dismiss (soft delete) ----------

  const handleDismissArtifact = useCallback(
    async (artifact: ResponseArtifactDTO) => {
      setOpenMenuId(null);
      const todoEl = document.querySelector(
        `[data-todo-id="${artifact.id}"]`,
      ) as HTMLElement | null;
      startCompletingAnimation(artifact.id, todoEl);

      setTimeout(() => {
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(artifact.id);
          return next;
        });
        setPendingArtifacts((prev) => prev.filter((a) => a.id !== artifact.id));
      }, 1100);

      try {
        const token = await getAccessTokenSilently();
        const request: UpdateResponseArtifactRequest = {
          artifactId: artifact.id,
          status: 'actioned',
          userResponse: { acknowledged: true, dismissed: true },
          source: 'todo_list',
        };
        await api.patch<UpdateResponseArtifactResponse>(
          `/response-artifacts/${artifact.id}`,
          request,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('Error dismissing artifact:', err);
      }
    },
    [getAccessTokenSilently, startCompletingAnimation],
  );

  const handleDismissUserTodo = useCallback(
    async (todo: UserTodoItem) => {
      setOpenMenuId(null);
      const todoEl = document.querySelector(
        `[data-todo-id="${todo.id}"]`,
      ) as HTMLElement | null;
      startCompletingAnimation(todo.id, todoEl);

      setTimeout(() => {
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(todo.id);
          return next;
        });
        setUserTodos((prev) => prev.filter((t) => t.id !== todo.id));
      }, 1100);

      try {
        const token = await getAccessTokenSilently();
        await api.delete(`/user-todos/${todo.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        console.error('Error dismissing user todo:', err);
      }
    },
    [getAccessTokenSilently, startCompletingAnimation],
  );

  // ---------- Set priority ----------

  const handleSetPriority = useCallback(
    async (item: UnifiedItem, priority: string | null) => {
      setOpenMenuId(null);
      const id = item.data.id;
      const typedPriority = priority
        ? (priority as 'high' | 'medium' | 'low')
        : undefined;

      // Optimistic update
      if (item.kind === 'userTodo') {
        setUserTodos((prev) =>
          prev.map((t) =>
            t.id === id ? { ...t, priority: typedPriority } : t,
          ),
        );
      } else {
        setPendingArtifacts((prev) =>
          prev.map((a) =>
            a.id === id ? { ...a, priority: typedPriority } : a,
          ),
        );
      }

      try {
        const token = await getAccessTokenSilently();
        if (item.kind === 'userTodo') {
          await api.patch(
            `/user-todos/${id}`,
            { priority },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } else {
          await api.patch(
            `/response-artifacts/${id}`,
            { priority },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        }
      } catch (err) {
        console.error('Error setting priority:', err);
      }
    },
    [getAccessTokenSilently],
  );

  // ---------- Unified sorted items ----------

  const activeUserTodos = userTodos.filter((t) => t.status === 'pending');
  const doneUserTodos = userTodos.filter((t) => t.status === 'complete');

  const sortedActiveItems = useMemo((): UnifiedItem[] => {
    const items: UnifiedItem[] = [];
    activeUserTodos.forEach((todo) =>
      items.push({ kind: 'userTodo', data: todo }),
    );
    pendingArtifacts.forEach((artifact) =>
      items.push({ kind: 'artifact', data: artifact }),
    );

    const PRIORITY_TIER: Record<string, number> = {
      high: 2,
      medium: 3,
      low: 4,
    };

    const getTier = (item: UnifiedItem): number => {
      const p = item.data.priority;
      if (p && PRIORITY_TIER[p] !== undefined) return PRIORITY_TIER[p];
      if (item.data.dueDate) return 5;
      return 6;
    };

    items.sort((a, b) => {
      const aTier = getTier(a);
      const bTier = getTier(b);
      if (aTier !== bTier) return aTier - bTier;
      // Within same tier, sort by due date ascending if present
      const aDue = a.data.dueDate;
      const bDue = b.data.dueDate;
      if (aDue && bDue) {
        const diff = new Date(aDue).getTime() - new Date(bDue).getTime();
        if (diff !== 0) return diff;
      } else if (aDue) {
        return -1;
      } else if (bDue) {
        return 1;
      }
      return (
        new Date(b.data.createdAt).getTime() -
        new Date(a.data.createdAt).getTime()
      );
    });

    return items;
  }, [activeUserTodos, pendingArtifacts]);

  // ---------- Item click handler ----------

  const handleItemClick = useCallback(
    (item: UnifiedItem) => {
      if (item.kind === 'artifact') {
        const artifact = item.data;
        const isBlankTodo = artifact.artifactType === 'blank_todo';
        if (isBlankTodo) return;

        // All other artifact types: open the ResponseArtifactModal
        openArtifactForItem(artifact);
      } else {
        // User todos: toggle on click if it's a setup plan
        const todo = item.data;
        if (todo.isSetupPlan && todo.status !== 'complete') {
          handleToggleUserTodo(todo);
        }
      }
    },
    [openArtifactForItem, handleToggleUserTodo],
  );

  // ---------- Render helpers ----------

  const renderLinkedResearch = (artifact: ResponseArtifactDTO) => {
    const linked = artifact.linkedArtifacts || [];
    if (linked.length === 0) return null;

    return (
      <>
        {linked.map((la) =>
          la.projectAppId ? (
            <div
              key={la.id}
              className='research-link'
              onClick={(e) => {
                e.stopPropagation();
                onNavigateToThread?.(
                  artifact.subThreadId,
                  artifact.parentThreadId,
                  la.projectAppId!,
                );
              }}
              role='button'
              tabIndex={0}
            >
              <DocumentTextIcon className='document-icon' />
              <span>{la.title || 'View Research'}</span>
            </div>
          ) : null,
        )}
      </>
    );
  };

  // Swipe-left-to-archive state for mobile (iOS-style)
  const swipeRef = useRef<{
    startX: number;
    startY: number;
    inner: HTMLElement;
    wrapper: HTMLElement;
    swiping: boolean;
  } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const wrapper = e.currentTarget as HTMLElement;
    const inner = wrapper.querySelector('.swipe-inner') as HTMLElement;
    if (!inner) return;
    swipeRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      inner,
      wrapper,
      swiping: false,
    };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const ref = swipeRef.current;
    if (!ref) return;
    const touch = e.touches[0];
    const dx = touch.clientX - ref.startX;
    const dy = Math.abs(touch.clientY - ref.startY);
    // Abort if scrolling vertically
    if (dy > 30 && !ref.swiping) {
      swipeRef.current = null;
      return;
    }
    // Only swipe left (negative)
    if (dx < -10) {
      if (!ref.swiping) {
        ref.swiping = true;
        ref.wrapper.classList.add('swiping');
      }
      ref.inner.style.transform = `translateX(${dx}px)`;
    }
  }, []);

  const handleTouchEnd = useCallback(
    (item: UnifiedItem) => {
      const ref = swipeRef.current;
      if (!ref) return;
      const dx =
        parseFloat(ref.inner.style.transform.replace(/[^0-9.-]/g, '')) || 0;
      // dx is negative for left swipe
      if (dx < -120) {
        // Dismiss — slide fully off left
        ref.inner.style.transform = 'translateX(-100%)';
        ref.inner.style.transition = 'transform 0.2s';
        setTimeout(() => {
          if (item.kind === 'artifact') {
            handleDismissArtifact(item.data as ResponseArtifactDTO);
          } else {
            handleDismissUserTodo(item.data as UserTodoItem);
          }
        }, 200);
      } else if (ref.swiping) {
        // Snap back
        ref.inner.style.transition = 'transform 0.2s';
        ref.inner.style.transform = 'translateX(0)';
        setTimeout(() => {
          if (ref.inner) {
            ref.inner.style.transition = '';
          }
          ref.wrapper.classList.remove('swiping');
        }, 200);
      }
      swipeRef.current = null;
    },
    [handleDismissArtifact, handleDismissUserTodo],
  );

  const renderItem = (item: UnifiedItem) => {
    const isArtifact = item.kind === 'artifact';
    const data = item.data;
    const id = data.id;
    const isCompleting = completingItems.has(id);
    const isSubmitted = submittedItems.has(id);

    const title = isArtifact
      ? (data as ResponseArtifactDTO).todoDescription ||
        (data as ResponseArtifactDTO).title ||
        'Pending task'
      : (data as UserTodoItem).description;

    const typeLabel = getItemTypeLabel(data, item.kind);
    const projectName = getProjectName(data, item.kind);
    const linkedResearch = isArtifact
      ? renderLinkedResearch(data as ResponseArtifactDTO)
      : null;

    const isBlankTodo =
      isArtifact && (data as ResponseArtifactDTO).artifactType === 'blank_todo';
    const isUserTodo = !isArtifact;
    const isCheckable = isBlankTodo || isUserTodo;

    // Determine whether clicking the whole row should open the artifact modal
    const isClickable =
      isArtifact && !isBlankTodo && !isSubmitted && !isCompleting;

    return (
      <div
        key={id}
        className={`your-task-item${isCompleting ? ' completing' : ''}${isSubmitted ? ' submitted' : ''}${isClickable ? ' clickable' : ''}${isMobile ? ' swipeable' : ''}`}
        data-todo-id={id}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => handleTouchEnd(item)}
      >
        {isMobile && (
          <div className='swipe-action'>
            <svg
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2'
                stroke='white'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        )}
        <div
          className={`swipe-inner${isClickable ? ' clickable' : ''}`}
          onClick={isClickable ? () => handleItemClick(item) : undefined}
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
        >
          <div className='item-left'>
            <div className='item-title-row'>
              {data.priority && PRIORITY_COLORS[data.priority] && (
                <span
                  className='priority-dot'
                  style={{
                    background: PRIORITY_COLORS[data.priority],
                  }}
                  title={`${data.priority} priority`}
                />
              )}
              <div className='item-title'>{title}</div>
            </div>
            {isCheckable ? (
              <>
                {linkedResearch && (
                  <div className='item-research'>{linkedResearch}</div>
                )}
                <div className='item-actions'>
                  {data.dueDate && (
                    <span
                      className={`due-date${isOverdue(data.dueDate) ? ' overdue' : ''}`}
                    >
                      <CalendarOutlineIcon className='calendar-icon' />
                      {formatDueDate(data.dueDate)}
                    </span>
                  )}
                  <button
                    className='mark-done-btn'
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isUserTodo) {
                        handleToggleUserTodo(data as UserTodoItem);
                      } else {
                        handleToggleBlankTodo(data as ResponseArtifactDTO);
                      }
                    }}
                  >
                    <svg
                      className={`check-circle${isCompleting ? ' checked' : ''}`}
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                    >
                      <circle
                        cx='8'
                        cy='8'
                        r='7.5'
                        fill={isCompleting ? '#489FD9' : 'white'}
                        stroke='#489FD9'
                      />
                      {isCompleting && (
                        <path
                          className='tick'
                          d='M4.5 8L7 10.5L11.5 6'
                          stroke='white'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          fill='none'
                        />
                      )}
                    </svg>
                    {isCompleting ? 'Done!' : 'Mark as done'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className='item-info'>
                  <div className='action-pill'>{typeLabel}</div>
                  {projectName && (
                    <span className='project-name'>{projectName}</span>
                  )}
                </div>
                {linkedResearch && (
                  <div className='item-research'>{linkedResearch}</div>
                )}
              </>
            )}
          </div>
          <div
            className='item-right'
            ref={openMenuId === id ? menuRef : undefined}
          >
            <button
              className='dots-btn'
              onClick={(e) => {
                e.stopPropagation();
                toggleMenu(id, e.currentTarget);
              }}
            >
              <VerticalDotsIcon />
            </button>
            {openMenuId === id &&
              createPortal(
                <div
                  className='your-tasks-dropdown-menu'
                  ref={dropdownRef}
                  style={{ top: menuPos.top, right: menuPos.right }}
                >
                  {isArtifact &&
                    !isCheckable &&
                    (() => {
                      const label = getOpenLabel(data as ResponseArtifactDTO);
                      return label ? (
                        <div
                          className='dropdown-item'
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(null);
                            handleItemClick(item);
                          }}
                        >
                          {label}
                        </div>
                      ) : null;
                    })()}
                  {!isMobile && (
                    <div className='dropdown-item has-submenu'>
                      <span>Set priority</span>
                      <span className='submenu-arrow'>›</span>
                      <div className='priority-submenu'>
                        {[
                          { value: 'high', label: 'High', color: '#EF4444' },
                          {
                            value: 'medium',
                            label: 'Medium',
                            color: '#F59E0B',
                          },
                          { value: 'low', label: 'Low', color: '#3B82F6' },
                          {
                            value: null,
                            label: 'No priority',
                            color: '#838b9b',
                          },
                        ].map((opt) => (
                          <div
                            key={opt.value ?? 'none'}
                            className={`priority-option${
                              (item.data.priority || null) === opt.value
                                ? ' active'
                                : ''
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetPriority(item, opt.value);
                            }}
                          >
                            <span
                              className='priority-dot'
                              style={{ background: opt.color }}
                            />
                            {opt.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div
                    className='dropdown-item dismiss'
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isArtifact) {
                        handleDismissArtifact(data as ResponseArtifactDTO);
                      } else {
                        handleDismissUserTodo(data as UserTodoItem);
                      }
                    }}
                  >
                    Dismiss
                  </div>
                </div>,
                document.body,
              )}
          </div>
        </div>
      </div>
    );
  };

  // ---------- Render ----------

  if (loading) {
    return (
      <div className='your-tasks-list'>
        <TasksSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className='your-tasks-list'>
        <ErrorState title='Failed to load tasks' subtitle={error} />
      </div>
    );
  }

  const hasActiveItems = sortedActiveItems.length > 0;
  const hasDoneItems = actionedArtifacts.length > 0 || doneUserTodos.length > 0;

  return (
    <div className='your-tasks-list'>
      {!isMobile && (
        <div className='header'>
          <span className='header-title'>Your tasks</span>
          <div className='header-actions'>
            <button className='clock-btn'>
              <ClockIcon />
            </button>
            <button className='new-btn' onClick={onAddTodo}>
              <PlusAddIcon />
              <span>New</span>
            </button>
          </div>
        </div>
      )}
      <div className='items-container'>
        {hasActiveItems && (
          <div className='items-list'>
            {sortedActiveItems.map((item) => renderItem(item))}
          </div>
        )}

        {!hasActiveItems && !hasDoneItems && (
          <div className='empty-state'>
            <p>Nothing to do</p>
            <small>Tasks from your assistant will appear here</small>
          </div>
        )}

        {!hasActiveItems && hasDoneItems && (
          <div className='empty-state'>
            <p>All done</p>
            <small>Your todo list is empty</small>
          </div>
        )}
      </div>

      {/* Research Still There Modal (blank todos with linked research) */}
      <ResearchStillThereModal
        show={researchModalOpen}
        artifact={blankTodoToComplete}
        onClose={() => {
          setResearchModalOpen(false);
          setBlankTodoToComplete(null);
        }}
        onConfirm={(artifact) => {
          setResearchModalOpen(false);
          setBlankTodoToComplete(null);
          completeBlankTodo(artifact);
        }}
      />

      {/* Response Artifact Modal */}
      <ResponseArtifactModal
        show={artifactModalOpen && !!fullArtifact && !loadingArtifact}
        onClose={closeArtifactModal}
        artifacts={modalArtifacts}
        initialIndex={0}
        onSubmitResponse={handleSubmitArtifactResponse}
        onOptimisticAction={handleOptimisticAction}
        readOnly={fullArtifact?.status === 'actioned'}
        navigateOnChat
      />
    </div>
  );
});

export default YourTasksList;
